import React, { useState } from 'react';
import { useAuth, apiCall } from '../AuthContext';
import { ShieldAlert, LogOut, Loader2 } from 'lucide-react';

/**
 * Sticky banner shown to admins while they are impersonating another user.
 * Reads `user._impersonated_by` (set by backend when JWT contains impersonated_by).
 * Calls POST /api/admin/stop-impersonating to revert.
 */
export default function ImpersonationBanner() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!user || user === false) return null;
  if (!user._impersonated_by) return null;

  const targetLabel = user.username || user.email || 'user';
  const adminLabel = user._impersonator_email || user._impersonator_username || 'admin';

  const stopImpersonating = async () => {
    setBusy(true);
    try {
      const { data } = await apiCall('/api/admin/stop-impersonating', { method: 'POST' });
      if (data?.access_token) {
        const stored = localStorage.getItem('xlabel_user');
        let parsed = {};
        try { parsed = stored ? JSON.parse(stored) : {}; } catch {}
        parsed.access_token = data.access_token;
        parsed.email = data.admin_email || parsed.email;
        localStorage.setItem('xlabel_user', JSON.stringify(parsed));
      }
      // Hard reload so AuthContext re-hydrates the admin user from /auth/me
      window.location.href = '/admin-users';
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to stop impersonating');
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-50 bg-amber-500 text-[#0F172A] border-b-2 border-amber-600 shadow-md"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <ShieldAlert className="w-4 h-4 flex-shrink-0" />
          <span>
            Impersonating <span className="underline" data-testid="impersonation-target">{targetLabel}</span>
            <span className="hidden sm:inline text-amber-900/80 font-normal ml-2">
              · signed in as admin {adminLabel}
            </span>
          </span>
          {user._impersonate_readonly && (
            <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-700 text-white text-[10px] uppercase tracking-wide">
              read-only
            </span>
          )}
        </div>
        <button
          onClick={stopImpersonating}
          disabled={busy}
          data-testid="stop-impersonating-button"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#0F172A] text-white hover:bg-black disabled:opacity-60 text-xs font-semibold transition-colors"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogOut className="w-3.5 h-3.5" />}
          Return to Admin
        </button>
      </div>
    </div>
  );
}
